/**
 * Helper functions for working with lead notes
 * These functions are used across Calls and Leads pages
 */

/**
 * Groups notes by lead_id
 * @param {Array} lead_notes - Array of note objects with lead_id property
 * @returns {Array} Array of arrays, where each sub-array contains notes for a specific lead_id
 */
export const groupByLeadId = (lead_notes) => {
    const notes = {};
    if (!lead_notes || !Array.isArray(lead_notes)) return [];
    lead_notes.forEach((item) => {
        if (item && item.lead_id) {
            const lead_id = item.lead_id;
            if (!notes[lead_id]) {
                notes[lead_id] = [];
            }
            notes[lead_id].push(item);
        }
    });
    return Object.values(notes);
}

/**
 * Gets notes for a specific lead_id
 * @param {Array} lead_notes - Array of note objects with lead_id property
 * @param {string|number} lead_id - The lead_id to filter notes by
 * @returns {Array} Array of notes for the specified lead_id
 */
export const getNotesGroupByLeadId = (lead_notes, lead_id) => {
    if (!lead_id) return [];
    const allNotesGroups = groupByLeadId(lead_notes);
    // Use == for type coercion (string vs number) to handle both string and number lead_ids
    return allNotesGroups.find(subArray => subArray && subArray.some(item => {
        const noteLeadId = item?.lead_id;
        return noteLeadId === lead_id;
    })) || [];
}

/**
 * Gets the count of notes for a specific lead_id
 * @param {Array} lead_notes - Array of note objects with lead_id property
 * @param {string|number} lead_id - The lead_id to count notes for
 * @param {boolean} useTypeCoercion - Whether to use == for type coercion (string vs number). Default: true
 * @returns {number} Count of notes for the specified lead_id
 */
export const getNotesCountByLeadId = (lead_notes, lead_id, useTypeCoercion = true) => {
    if (!lead_id || !lead_notes || !Array.isArray(lead_notes)) return 0;
    // Match notes by lead_id
    // Use == for type coercion (string vs number) by default, but allow strict ===
    return lead_notes.filter((item) => {
        if (!item) return false;
        const noteLeadId = item.lead_id;
        if (useTypeCoercion) {
            return noteLeadId == lead_id;
        }
        return noteLeadId === lead_id;
    })?.length || 0;
}

/**
 * Gets the count of notes for a call (uses call_notes directly from call object)
 * @param {Array} call_notes - Array of call note objects from Redux store
 * @param {Object} call - Call object with id and call_notes property
 * @param {boolean} useTypeCoercion - Whether to use == for type coercion (string vs number). Default: true
 * @returns {number} Count of notes for the call
 */
/**
 * Gets the count of notes for a call by call_id (similar to getNotesCountByLeadId)
 * @param {Array} call_notes - Array of call note objects from Redux store
 * @param {string|number} call_id - The call_id to count notes for
 * @param {boolean} useTypeCoercion - Whether to use == for type coercion (string vs number). Default: true
 * @returns {number} Count of notes for the specified call_id
 */
export const getNotesCountByCall = (call_notes, call_id, useTypeCoercion = true) => {
    if (!call_id || !call_notes || !Array.isArray(call_notes)) return 0;
    // Match notes by quote_call_id (similar to how leads match by lead_id)
    // Use == for type coercion (string vs number) by default, but allow strict ===
    return call_notes.filter((item) => {
        if (!item) return false;
        const noteCallId = item.quote_call_id;
        if (useTypeCoercion) {
            return noteCallId == call_id;
        }
        return noteCallId === call_id;
    })?.length || 0;
}

/**
 * Extracts notes from leads/calls array and formats them for Redux store
 * @param {Array} items - Array of leads or calls from API response
 * @returns {Object} Object with { notes: [...], processedLeadIds: [...] } format for reducer
 */

export const extractNotesFromLeadsOrCalls = (items) => {
    if (!Array.isArray(items) || items.length === 0) {
        return { notes: [], processedLeadIds: [] };
    }
    
    const allNotes = [];
    const processedLeadIds = new Set();
    const seenNoteIds = new Set(); // Track note IDs to prevent duplicates
    
    items.forEach(item => {
        // item.lead_id is the lead_id from the lead object for calls page and details page
        // item?.id is the lead_id from the item object for leads page and details page
        const leadId = item.lead_id || item?.id;
        if (leadId) {
            processedLeadIds.add(leadId);
            const notes = item.lead_notes || item.lead?.lead_notes || [];
            if (Array.isArray(notes) && notes.length > 0) {
                notes.forEach(note => {

                    if (note?.id && !seenNoteIds.has(note.id)) {
                        seenNoteIds.add(note.id);
                        allNotes.push({
                            ...note,
                            lead_id: leadId
                        });
                    }
                });
            }
        }
    });
    
    return {
        notes: allNotes,
        processedLeadIds: Array.from(processedLeadIds)
    };
}

/**
 * Extracts call notes from calls array and formats them for Redux store
 * @param {Array} calls - Array of calls from API response
 * @returns {Object} Object with { notes: [...], processedCallIds: [...] } format for reducer
 */
export const extractCallNotesFromCalls = (calls) => {
    if (!Array.isArray(calls) || calls.length === 0) {
        return { notes: [], processedCallIds: [] };
    }
    
    const allNotes = [];
    const processedCallIds = new Set();
    const seenNoteIds = new Set(); // Track note IDs to prevent duplicates
    
    calls.forEach(call => {
        // Use quote_call.id if available (for call detail pages), otherwise use call.id
        const callId = call?.quote_call?.id || call?.id;
        if (callId) {
            processedCallIds.add(callId);
            // Get call_notes from call.call_notes or call.quote_call.call_notes
            const notes = call.call_notes || call?.quote_call?.call_notes || [];
            if (Array.isArray(notes) && notes.length > 0) {
                notes.forEach(note => {
                    if (note?.id && !seenNoteIds.has(note.id)) {
                        seenNoteIds.add(note.id);
                        allNotes.push({
                            ...note,
                            quote_call_id: callId
                        });
                    }
                });
            }
        }
    });
    
    return {
        notes: allNotes,
        processedCallIds: Array.from(processedCallIds)
    };
}

/**
 * Gets notes for a specific call_id from call_notes array
 * @param {Array} call_notes - Array of call note objects with quote_call_id property
 * @param {string|number} call_id - The call_id to filter notes by
 * @returns {Array} Array of notes for the specified call_id
 */
export const getNotesGroupByCallId = (call_notes, call_id) => {
    if (!call_id || !call_notes || !Array.isArray(call_notes)) return [];
    return call_notes.filter((note) => {
        const noteCallId = note?.quote_call_id;
        return noteCallId == call_id;
    });
}

/**
 * Prepares modal state for opening notes modal
 * This is a common function used across Leads, Calls, and CallPlayback components
 * @param {Array} lead_notes - Array of all lead notes from Redux store (for leads)
 * @param {Array} call_notes - Array of all call notes from Redux store (for calls)
 * @param {string|number} lead_id - The lead_id to get notes for (for leads)
 * @param {number|null} call_id - Optional call_id for calls (null for leads)
 * @returns {Object|null} Modal state object with { action: true, notes: [...], lead_id: ..., call_id: ... } or null if no lead_id/call_id
 */
export const prepareNotesModalState = (lead_notes, lead_id, call_id = null, call_notes = null) => {
    // For calls, use call_notes and call_id
    if (call_id !== null && call_id !== undefined && call_notes) {
        const notes = getNotesGroupByCallId(call_notes, call_id);
        return {
            action: true,
            notes: notes,
            lead_id: lead_id, // Keep lead_id for backward compatibility if needed
            call_id: call_id
        };
    }
    // For leads, use lead_notes and lead_id
    if (!lead_id) {
        return null;
    }
    const notes = getNotesGroupByLeadId(lead_notes, lead_id);
    const modalState = {
        action: true,
        notes: notes,
        lead_id: lead_id
    };
    // Only include call_id if it's provided (for calls, not leads)
    if (call_id !== null && call_id !== undefined) {
        modalState.call_id = call_id;
    }
    return modalState;
}

/**
 * Opens the notes modal by preparing modal state and calling the setter function
 * This is a common function used across Leads, Calls, and CallPlayback components
 * @param {Array} lead_notes - Array of all lead notes from Redux store (for leads)
 * @param {string|number} lead_id - The lead_id to get notes for
 * @param {Function} setModalState - Function to set the modal state (e.g., setLeadNoteModal, setCallNoteModal)
 * @param {number|null} call_id - Optional call_id for calls (null for leads)
 * @param {Array} call_notes - Optional array of call notes from Redux store (for calls)
 * @returns {void}
 */
export const openNotesModal = (lead_notes, lead_id, setModalState, call_id = null, call_notes = null) => {
    if (!setModalState) {
        return;
    }
    // For calls, we need either call_id or lead_id
    if (call_id !== null && call_id !== undefined) {
        const modalState = prepareNotesModalState(lead_notes, lead_id, call_id, call_notes);
        if (modalState) {
            setModalState(modalState);
        }
        return;
    }
    // For leads, we need lead_id
    if (!lead_id) {
        return;
    }
    const modalState = prepareNotesModalState(lead_notes, lead_id, call_id, call_notes);
    if (modalState) {
        setModalState(modalState);
    }
}

export const getNotesCountText = ({ call_notes, lead_notes, row, call_id, lead_id, isCallDetailsPage }) => {
    let count = 0;
    
    // Pattern 1: If row is provided (Calls.js pattern), extract call_id from row
    if (row) {
        const extractedCallId = row?.quote_call?.id || row?.id;
        if (extractedCallId) {
            count = getNotesCountByCall(call_notes || [], extractedCallId);
        }
    }
    // Pattern 2: If isCallDetailsPage is true and call_id is available (LeadSnapshot.js pattern for calls)
    else if (isCallDetailsPage && call_id) {
        count = getNotesCountByCall(call_notes || [], call_id);
    }
    // Pattern 3: If lead_id is provided (LeadSnapshot.js pattern for leads)
    else if (lead_id) {
        count = getNotesCountByLeadId(lead_notes || [], lead_id);
    }
    // Pattern 4: If explicit call_id is provided (fallback)
    else if (call_id) {
        count = getNotesCountByCall(call_notes || [], call_id);
    }
    
    return `(${count})` + (count !== 1 ? " Notes" : " Note");
}

